import { autoSuggest, productSearch } from "@bloomreach/discovery-web-sdk";
import { createQuery } from "@tanstack/svelte-query";
import { debounce } from "es-toolkit";
import type { AutosuggestOptions, SuggestResponse } from "@bloomreach/discovery-web-sdk";
import { readBrUid } from "~/lib/bloomreach/read-br-uid";
import { buildProductSearchOptions } from "~/lib/bloomreach/query-builders";
import {
  resolveBloomreachCredentials,
  toSdkConfiguration,
  BLOOMREACH_STALE_TIME_MS,
} from "~/lib/bloomreach/config";
import type { BloomreachConfig, BloomreachResponse } from "~/lib/bloomreach/types";

// ─── Constants ────────────────────────────────────────────────────────────────

// Matches the PLP debounce — `create-search-listing.svelte.ts:28` is the precedent.
const DEBOUNCE_MS = 300;

// High-intent 2-char queries exist in DUSK's segment (e.g. "tv" -> TV units). 1 char is
// too noisy; 3 would drop legitimate short-form queries.
const MIN_QUERY_LENGTH = 2;

// Autosuggest shows a fixed top-N, not paginated. Keep well below the PLP default (48).
// Exported because the panel's loading skeleton renders this many placeholder cards —
// a different number there is a guaranteed layout shift when the results land.
export const AUTOSUGGEST_PRODUCT_ROWS = 8;

// ─── Types ────────────────────────────────────────────────────────────────────

export type AutosuggestConfig = Pick<BloomreachConfig, "accountId" | "domainKey" | "authKey">;

// ─── Factory ─────────────────────────────────────────────────────────────────

/**
 * Reactive autosuggest state machine.
 *
 * Fires `autoSuggest` (popular searches) and `productSearch` (cards) in parallel from one
 * debounced query — never as a waterfall. Both are gated on a 2-character minimum and a
 * 300ms debounce, and query state starts empty rather than from the URL, so no API call
 * fires on mount.
 *
 * @see .agents/instructions/islands.md — QueryClient bootstrap
 */
export function createAutosuggest(config: AutosuggestConfig) {
  // Destructured so primitives can be included in queryKeys — satisfies @tanstack/query/exhaustive-deps.
  // sdkConfig is created inside each queryFn (same pattern as create-search-listing.svelte.ts).
  const { accountId, domainKey, authKey } = resolveBloomreachCredentials(config);
  const credentialsPresent = Boolean(accountId && domainKey);

  // Captured once at factory time — document.referrer is stable per page load.
  // Guarded for SSR consistency: queryFns are browser-only, but the options objects
  // are constructed eagerly so the guard keeps the pattern consistent with readBrUid().
  const _refUrl = typeof document !== "undefined" && document.referrer ? document.referrer : null;

  // ── Query state ──────────────────────────────────────────────────────────────
  // Both query states start empty — the preserved URL value populates _displayValue only
  // (via seedDisplayValue). No API call fires until the visitor types.
  let _pendingQuery: string = $state("");
  let _debouncedQuery: string = $state("");

  // ── Display value (input binding) ────────────────────────────────────────────
  // Tracks what the input element shows. Updated synchronously by `set query` and
  // `setQueryImmediately`. Can be pre-seeded with the preserved ?q= value on DOM
  // takeover WITHOUT triggering a search (see `seedDisplayValue`).
  let _displayValue: string = $state("");

  const _debouncedUpdate = debounce((v: string) => {
    _debouncedQuery = v;
  }, DEBOUNCE_MS);

  // Cancel the pending debounce on cleanup to prevent stale state updates after teardown.
  $effect(() => {
    _debouncedUpdate(_pendingQuery);
    return () => _debouncedUpdate.cancel();
  });

  // Both queries share this gate — derived so it's always in sync with _debouncedQuery.
  const isEnabled = $derived(credentialsPresent && _debouncedQuery.length >= MIN_QUERY_LENGTH);

  // ── autoSuggest query ────────────────────────────────────────────────────────
  // catalog_views, not domain_key — required by the v2 autosuggest endpoint.
  const suggestQuery = createQuery<SuggestResponse>(() => ({
    queryKey: ["autosuggest-suggest", _debouncedQuery, accountId, domainKey, authKey, _refUrl],
    queryFn: () => {
      const sdkConfig = toSdkConfiguration({ accountId, domainKey, authKey });
      const options: AutosuggestOptions = {
        url: window.location.href,
        _br_uid_2: readBrUid(),
        catalog_views: domainKey,
        ...(_refUrl ? { ref_url: _refUrl } : {}),
      };
      return autoSuggest(_debouncedQuery, sdkConfig, options);
    },
    enabled: isEnabled,
    staleTime: BLOOMREACH_STALE_TIME_MS,
  }));

  // ── productSearch query ──────────────────────────────────────────────────────
  // Independent of suggestQuery, which is what keeps the two parallel.
  const productsQuery = createQuery<BloomreachResponse>(() => ({
    queryKey: ["autosuggest-products", _debouncedQuery, accountId, domainKey, authKey],
    queryFn: () => {
      const sdkConfig = toSdkConfiguration({ accountId, domainKey, authKey });
      const options = buildProductSearchOptions({
        searchQuery: _debouncedQuery,
        currentUrl: window.location.href,
        rows: AUTOSUGGEST_PRODUCT_ROWS,
      });
      return productSearch(
        _debouncedQuery,
        sdkConfig,
        options,
      ) as unknown as Promise<BloomreachResponse>;
    },
    enabled: isEnabled,
    staleTime: BLOOMREACH_STALE_TIME_MS,
  }));

  // ── Public API ───────────────────────────────────────────────────────────────

  return {
    // One-way: the input writes back through `oninput -> set query`. A `bind:value` setter
    // would bypass seedDisplayValue's separation of "show preserved text" from "arm the
    // debounce".
    get displayValue(): string {
      return _displayValue;
    },

    // The 300ms debounce propagates this to `_debouncedQuery`, which gates both calls.
    get query(): string {
      return _pendingQuery;
    },
    set query(v: string) {
      _displayValue = v;
      _pendingQuery = v;
    },

    /**
     * Shows a preserved ?q= value without searching for it — the DOM takeover keeps the
     * native input's text visible while the machine query stays empty until the visitor
     * types. `_pendingQuery` and `_debouncedQuery` are deliberately untouched.
     */
    seedDisplayValue(v: string): void {
      _displayValue = v;
    },

    /**
     * Sets both queries at once, bypassing the debounce — for programmatic changes that
     * need instant feedback: Escape-to-clear, selecting a suggestion, or a takeover
     * landing a query the visitor has already typed.
     */
    setQueryImmediately(v: string): void {
      _debouncedUpdate.cancel();
      _displayValue = v;
      _pendingQuery = v;
      _debouncedQuery = v;
    },

    // Panel open gate: the settled query has reached the minimum length.
    get isOpen(): boolean {
      return isEnabled;
    },

    // Raw TanStack query handles — let the panel own loading/error rendering.
    get suggestQuery() {
      return suggestQuery;
    },
    get productsQuery() {
      return productsQuery;
    },

    // ── Derived data (panel-ready) ─────────────────────────────────────────────

    /** Query suggestions driving the popular searches. */
    get querySuggestions() {
      const groups = suggestQuery.data?.suggestionGroups ?? [];
      return groups.flatMap((g) => g.querySuggestions ?? []);
    },

    /** Product docs for the card row. */
    get products() {
      return productsQuery.data?.response?.docs ?? [];
    },

    /** The term Bloomreach silently corrected the query to, or null. */
    get autocorrectQuery(): string | null {
      return productsQuery.data?.autoCorrectQuery ?? null;
    },
  };
}

export type Autosuggest = ReturnType<typeof createAutosuggest>;
